import { defineStore } from "pinia";
import { computed, ref } from "vue";
import {
  getClassDetails,
  getTeachingMaterials,
  getMaterialsByDivertUser,
  setTeachingMaterials as setTeachingMaterialsAPI,
  setMaterialsByDivertUser,
  getMeterialRemarkDetail,
  getIsDivertUser,
} from "@/api/classtable";

export const useClassDetailStore = defineStore("classDetail", () => {
  const info = ref({});
  const teachingMaterials = ref([]);
  const loading = ref(false);
  const loadingMaterials = ref(false);
  const diver = ref(false);
  const lessonPlan = ref({
    dialog: false,
    clid: 0,
    classStatus: "",
    classTime: "",
    edit: "",
  });
  /** Agora classroom payload (Vue2 classDetail.agora) */
  const agora = ref({});

  const hasMaterials = computed(() => !!info.value?.GetUserCatagory);
  const UCID = computed(() =>
    hasMaterials.value ? info.value.GetUserCatagory.UCID : null
  );

  const classStatus = computed(() => {
    const isFinished = info.value?.IsOverClass || false;
    const isCorrect = info.value?.SID > 0;
    if (!isFinished && !isCorrect) return "unfinished";
    if (isFinished && isCorrect) return "finished";
    return "incorrectFinished";
  });

  const currentLesson = computed(() => {
    const materials = teachingMaterials.value;
    if (!materials?.length) return null;
    const hasCSID = materials.find((m) => m.CSID > 0);
    if (hasCSID) return hasCSID;
    const lastMatched = materials
      .slice()
      .reverse()
      .find((m) => m.USID > 0 || m.USAID > 0);
    if (lastMatched) {
      return materials.find((m) => m.RowNum === lastMatched.RowNum + 1);
    }
    return materials.find((m) => m.IsRemark === "True" || m.IsRemark === true);
  });

  const meterialList = computed(() => {
    if (!hasMaterials.value) return [];
    const current = currentLesson.value;
    const currentRow = current ? current.RowNum : -1;
    let normalLessonNumber = 1;
    return teachingMaterials.value.map((m) => {
      let lessonType = 4;
      if (m.ClassType > 0) lessonType = 3;
      else if (m.USID > 0) lessonType = 1;
      else if (!(m.IsRemark === "True" || m.IsRemark === true)) lessonType = 2;

      const title =
        lessonType === 3
          ? "Test Lesson"
          : lessonType === 2
            ? "Teaching Plan"
            : normalLessonNumber++;

      return {
        title,
        isCurrentLesson: diver.value ? m.IsCurrentCourseware : m.RowNum === currentRow,
        lessonName: m.SubjectName,
        lessonType,
        originData: m,
        SID: m.SID,
        CatId: m.CatId,
        IsTest: m.IsTest,
        UnitID: m.UnitID,
        Sort: "Unit " + m.Sort,
        VipNumber: "Lesson " + m.VipNumber,
        TestNumber: "Lesson " + m.TestNumber,
      };
    });
  });

  const userCatagory = computed(() => info.value?.GetUserCatagory || {});

  async function fetchInfo(id) {
    loading.value = true;
    try {
      const res = await getClassDetails(id);
      info.value = res || {};
    } finally {
      loading.value = false;
    }
  }

  async function hasDiverUser() {
    if (!hasMaterials.value) return;
    try {
      const res = await getIsDivertUser(info.value.StuUID);
      diver.value = !!res;
    } catch (_) {
      diver.value = false;
    }
  }

  async function fetchMeterials(options = {}) {
    if (!hasMaterials.value) return;
    const silent = !!options.silent;
    if (!silent) loadingMaterials.value = true;
    try {
      const res = diver.value
        ? await getMaterialsByDivertUser(info.value.CLID, info.value.StuUID)
        : await getTeachingMaterials(UCID.value, info.value.istext);
      teachingMaterials.value = Array.isArray(res) ? res : [];
    } finally {
      if (!silent) loadingMaterials.value = false;
    }
  }

  async function setTeachingMaterials({ SID, CatId }) {
    if (!hasMaterials.value) {
      throw new Error("Please wait for Teaching Material fully loaded.");
    }
    if (diver.value) {
      await setMaterialsByDivertUser(info.value.CLID, info.value.StuUID, SID, CatId);
    } else {
      await setTeachingMaterialsAPI({
        UCID: info.value.GetUserCatagory.UCID,
        SID,
        UID: info.value.StuUID,
        CLID: info.value.CLID,
      });
    }
    // Silent refresh keeps page height / scroll position stable
    await fetchMeterials({ silent: true });
  }

  function fetchMaterialRemark({ SID, UCID: ucid }) {
    return getMeterialRemarkDetail({ SID, UCID: ucid });
  }

  function openLessonPlanDialog(payload) {
    lessonPlan.value = {
      dialog: true,
      clid: payload.clid,
      classStatus: payload.classStatus || "",
      classTime: payload.classTime || "",
      edit: payload.edit || "",
    };
  }

  function closeLessonPlanDialog() {
    lessonPlan.value = { ...lessonPlan.value, dialog: false };
  }

  function setTeachingAgora(data) {
    agora.value = data || {};
  }

  return {
    info,
    teachingMaterials,
    loading,
    loadingMaterials,
    diver,
    lessonPlan,
    agora,
    hasMaterials,
    UCID,
    classStatus,
    currentLesson,
    meterialList,
    userCatagory,
    fetchInfo,
    hasDiverUser,
    fetchMeterials,
    setTeachingMaterials,
    fetchMaterialRemark,
    openLessonPlanDialog,
    closeLessonPlanDialog,
    setTeachingAgora,
  };
});
